import { srOnly } from '../organic/motion';
import OrganicEmptyState from '../organic/OrganicEmptyState.jsx';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import { TYPE_ICON } from '../specimens/ProvenanceGraph';
import { Code, Taxon } from './cells';
import { ownCode } from './IsolatesTable';
import { rowActions } from './quickActions';
import { STAGES, stateStyle } from './stages';
import { PAGE_SIZE } from './useIsolatesOverview';
import * as S from '../specimens/styles';

/*
 * The canvas's isolates view: one pipeline per row of the master table
 * below -- the same page of the same rows (the hub hands both the one
 * GET /specimens/overview answer), so nothing here is read twice and the
 * two can never disagree.
 *
 * Each row: the isolate (its ID, lab code and taxon), then its five stages
 * as nodes wired one to the next, then the one thing it needs next (its
 * primary quick action, as the table's Actions cell offers it). A node says
 * its state by glyph, border style and words as well as colour, and the
 * wire into a node is drawn in the state of the stage before it (index.css
 * .hub-pipe__node[data-from]): solid once done, a moving dash while in
 * progress, dashed before it starts, dotted where the reader is not shown,
 * red at a problem.
 *
 * Pressing a row (its header or any node) highlights its row in the table
 * and brings it into view (IsolateHub's ?hl=); pressing it again lifts the
 * highlight. The header is the one keyboard stop per row: five more per
 * isolate would make a page of 25 a long walk, so the nodes take a pointer
 * only and say everything they show to a screen reader in words.
 */

/** What a stage is drawn with: the origin's own icon for the collection. */
function stageIcon(stage, item) {
  switch (stage) {
    case 'collection':
      if (item.origin_table === 'field_collection') return '📷';
      if (item.origin_table === 'external_collections') return '📦';
      // Known only through its strain, or with no origin linked.
      return '🧪';
    case 'culture': return TYPE_ICON.lab_subculture;
    case 'dna': return TYPE_ICON.dna_analysis;
    case 'identification': return '🔎';
    case 'bank': return TYPE_ICON.storage;
    default: return '';
  }
}

/**
 * One stage of an isolate, as PipelineStrip (cells.js) reads it: a stage the
 * server sent as null is one this reader is not shown -- unknown, never "not
 * started" -- and an isolate with no pipeline at all has every stage still
 * to come.
 */
export function stageOf(item, stage) {
  const pipeline = item?.pipeline;
  const raw = pipeline?.[stage];
  if (pipeline && raw === null) return { state: 'unknown', detail: null, record: null };
  return { state: raw?.state || 'pending', detail: raw?.detail || null, record: raw?.record || null };
}

function StageNode({ stage, item, previous, t, onPress }) {
  const { state, detail, record } = stageOf(item, stage);
  const look = stateStyle(state);
  const name = t(`stage.${stage}`);
  const said = state === 'unknown' ? t('state.unknown') : detail ? t(`detail.${stage}.${detail}`) : t(`state.${state}`);
  const code = record?.code || '';
  return (
    <li className="hub-pipe__node" data-state={state} data-stage={stage} data-from={previous || undefined}
      title={code ? `${name}: ${said} (${code})` : `${name}: ${said}`} onClick={onPress}
      style={{ background: look.bg, color: look.fg, borderStyle: look.border }}
      data-testid={`hub-pipe-node-${stage}-${item.specimen_id}`}>
      <span className="hub-pipe__mark" aria-hidden="true">
        <span className="hub-pipe__icon">{stageIcon(stage, item)}</span>
        <span className="hub-pipe__glyph">{look.glyph}</span>
      </span>
      <span className="hub-pipe__body">
        <span className="hub-pipe__stage" aria-hidden="true">{name}</span>
        <span className="hub-pipe__detail" aria-hidden="true">{said}</span>
        <span style={srOnly}>{t('pipes.stage', { stage: name, state: said })}</span>
        {/* The record's code, without the isolate's own code again in front. */}
        {code && <span className="hub-pipe__code"><Code>{ownCode(code, item.specimen_code)}</Code></span>}
      </span>
    </li>
  );
}

function PipeRow({ item, pressed, onHighlight, onPeek, onAction, user, node, t }) {
  const id = item.specimen_id;
  const press = () => onHighlight(id);
  // A stage node opens that stage in place (IsolatePeek); without a peek
  // handler it presses the row, as it used to.
  const peek = (stage) => (onPeek ? onPeek(id, stage) : press());
  const next = rowActions(item, user, { node }).find((a) => a.primary);
  return (
    <li className={`hub-pipe${pressed ? ' hub-pipe--hl' : ''}`} data-testid={`hub-pipe-row-${id}`}>
      <button type="button" className="hub-pipe__head" aria-pressed={pressed} aria-controls={`hub-tr-${id}`}
        title={pressed ? t('tip.pipes.rowPressed') : t('tip.pipes.row')} onClick={press}
        data-testid={`hub-pipe-${id}`}>
        <span className="hub-pipe__codes">
          <Code strong>{item.specimen_code}</Code>
          <Code>{item.lab_code}</Code>
        </span>
        <span className="hub-pipe__taxon"><Taxon name={item.taxon_name} /></span>
      </button>
      <ol className="hub-pipe__stages" aria-label={t('columns.pipeline')}>
        {STAGES.map((stage, i) => (
          <StageNode key={stage} stage={stage} item={item} t={t} onPress={() => peek(stage)}
            previous={i > 0 ? stageOf(item, STAGES[i - 1]).state : null} />
        ))}
      </ol>
      {next && (
        <div className="hub-pipe__next">
          <span className="hub-pipe__next-label">{t('pipes.next')}</span>
          <button type="button" className="hub-pipe__action" onClick={() => onAction(next.key, item)}
            title={t(`tip.action.${next.key}`)} data-testid={`hub-pipe-action-${next.key}-${id}`}>
            {t(`action.${next.key}`)}
          </button>
        </div>
      )}
    </li>
  );
}

export default function IsolatePipelines({
  data, params, onParams, highlightId, onHighlight, onAction, user, t, onPeek = null,
}) {
  const { items = [], total = 0, loading, error, reload } = data;
  const page = Math.max(0, Number(params.page) || 0);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE + items.length);
  // The table's own pager, from up here: the same page of the same rows. A
  // highlighted row is not on the next page, so its highlight goes too.
  const turn = (target) => onParams({ page: target || null, open: null, hl: null });

  return (
    <section className="hub-pipes-panel" aria-labelledby="hub-pipes-title" data-testid="hub-pipes"
      aria-busy={loading || undefined}>
      <div className="hub-pipes__head">
        <h3 id="hub-pipes-title" className="hub-pipes__title">{t('pipes.title')}</h3>
        {items.length > 0 && (
          <span style={S.hint} data-testid="hub-pipes-range">{t('pipes.range', { from, to, total })}</span>
        )}
        {total > PAGE_SIZE && (
          <div className="hub-pipes__pager" role="group" aria-label={t('toolbar.pages')}>
            <button type="button" className="hub-mini" disabled={page <= 0} title={t('tip.prev')}
              onClick={() => turn(page - 1)} data-testid="hub-pipes-prev">{t('toolbar.prev')}</button>
            <button type="button" className="hub-mini" disabled={page + 1 >= pages} title={t('tip.next')}
              onClick={() => turn(page + 1)} data-testid="hub-pipes-next">{t('toolbar.next')}</button>
          </div>
        )}
      </div>
      <p style={{ ...S.hint, margin: '2px 0 0' }}>{t('pipes.hint')}</p>

      {error && (
        <div role="alert" style={{ ...S.error, display: 'flex', gap: 10, alignItems: 'center' }}>
          {error}
          <button type="button" className="hub-mini" onClick={reload} title={t('tip.retry')}
            data-testid="hub-pipes-retry">{t('retry')}</button>
        </div>
      )}

      {loading && !items.length && (
        <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />
      )}

      {!loading && !error && items.length === 0 && (
        <OrganicEmptyState kind="plates" compact title={t('pipes.empty')} hint={t('pipes.emptyHint')} />
      )}

      {items.length > 0 && (
        <ol className="hub-pipes" data-testid="hub-pipes-list">
          {items.map((item) => (
            <PipeRow key={item.specimen_id} item={item} pressed={highlightId === item.specimen_id} onPeek={onPeek}
              onHighlight={onHighlight} onAction={onAction} user={user} node={params.node} t={t} />
          ))}
        </ol>
      )}
    </section>
  );
}
